import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Users, X, CalendarHeart } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>{children}</motion.div>
);

const fieldCls = "w-full bg-white border border-[#D8CEBE] rounded-md px-4 py-3 text-[#2A2825] font-body outline-none focus:border-[#A9724E] transition-colors";

function ClubModal({ tier, onClose }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/payments/checkout`, {
        lookup_key: tier.key, name: form.name.trim(), email: form.email.trim(), origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout. Please try again.");
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="club-join-modal" className="bg-[#F4EFE6] text-[#2A2825] rounded-2xl w-full max-w-md p-6 md:p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <p className="tracking-overline text-[10px] uppercase text-[#A9724E]">Outpoured Community · {tier.label}</p>
          <button onClick={onClose} className="text-[#8a7a6a] hover:text-[#A9724E]" data-testid="club-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <h3 className="font-serif-display text-2xl md:text-3xl font-light mb-4">Join the community</h3>
        <div data-testid="next-gathering" className="flex items-center gap-3 rounded-xl border border-[#A9724E]/30 bg-[#EAE3D9]/60 px-4 py-3 mb-5">
          <CalendarHeart className="w-5 h-5 text-[#A9724E] shrink-0" strokeWidth={1.5} />
          <p className="text-sm font-body text-[#5E4B3C]"><span className="text-[#8a7a6a]">Next gathering:</span> <span className="text-[#2A2825] font-medium">{tier.gathering}</span></p>
        </div>
        <p className="text-sm text-[#5E4B3C] font-body mb-6">{tier.price}/month · cancel anytime. You'll head to a secure checkout next.</p>
        <form onSubmit={submit} className="space-y-4">
          <input data-testid="club-name" className={fieldCls} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input data-testid="club-email" type="email" className={fieldCls} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <button type="submit" disabled={loading} data-testid="club-checkout-submit"
            className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-7 py-3.5 text-sm tracking-wide transition-colors duration-500 disabled:opacity-50">
            {loading ? "Taking you to checkout…" : `Continue · ${tier.price}/mo`} <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} />
          </button>
        </form>
      </div>
    </div>
  );
}

const CLUB = [
  { key: "club_women_monthly", label: "Women", price: "$25", who: "For one woman", gathering: "September 27, 2026" },
  { key: "club_couples_monthly", label: "Couples", price: "$45", who: "For two — a couple", gathering: "October 4, 2026" },
];

export default function WaysToBegin() {
  const [modal, setModal] = useState(null);
  return (
    <section id="community" data-testid="ways-to-begin" className="py-16 md:py-24 bg-[#EAE3D9]/40 border-y border-[#D1C9BE] scroll-mt-16">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3 text-center">The Club · monthly</p></Reveal>
        <Reveal delay={0.06}><h2 className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl leading-tight mb-4 text-center">Outpoured Community.</h2></Reveal>
        <Reveal delay={0.1}><p className="text-[#5E4B3C] font-body text-center max-w-xl mx-auto mb-10 md:mb-12">Gather with the community month to month.</p></Reveal>

        <Reveal delay={0.14}>
          <div data-testid="tier-club" className="flex flex-col bg-[#2A2825] text-[#F4EFE6] grain rounded-2xl p-7 md:p-10 relative overflow-hidden">
            <span className="inline-flex items-center gap-2 self-start rounded-full bg-white/10 text-[#E8B486] tracking-overline text-[10px] uppercase px-3 py-1.5 mb-5"><Users className="w-3.5 h-3.5" strokeWidth={1.5} />The Club · monthly</span>
            <h3 className="font-serif-display text-2xl md:text-3xl font-light mb-3">Outpoured Club</h3>
            <p className="text-[#D9CFC2] font-body leading-relaxed mb-4">A community for those who want connection without the full six-month cohort — built around <span className="text-[#F4EFE6]">monthly hangouts, daily Go &amp; Do challenges, and monthly connect calls</span>, in person and online.</p>
            <ul className="text-[#C9BEB0] font-body text-sm space-y-1.5 mb-7">
              <li>· Monthly hangouts — in person and online</li>
              <li>· Daily Go &amp; Do challenges to practice all week</li>
              <li>· Monthly connect calls with the community</li>
              <li>· First to know, first invited — cancel anytime</li>
            </ul>
            <div className="grid sm:grid-cols-2 gap-3">
              {CLUB.map((t) => (
                <button key={t.key} data-testid={`club-join-${t.label.toLowerCase()}`} onClick={() => setModal(t)}
                  className="group flex items-center justify-between gap-3 rounded-xl border border-[#E8B486]/40 hover:bg-white/10 px-5 py-4 text-left transition-colors duration-500">
                  <span>
                    <span className="block font-serif-display text-lg">{t.label}</span>
                    <span className="block text-xs text-[#C9BEB0] font-body">{t.who}</span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="font-serif-display text-xl text-[#E8B486]">{t.price}<span className="text-sm text-[#C9BEB0]">/mo</span></span>
                    <ArrowRight className="w-4 h-4 text-[#E8B486] transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
      {modal && <ClubModal tier={modal} onClose={() => setModal(null)} />}
    </section>
  );
}
